import { filterBy, SortDescriptor } from '@progress/kendo-data-query';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { DateInput, DateInputProps, DateRangePicker, SelectionRange } from '@progress/kendo-react-dateinputs';
import { MultiSelect, MultiSelectFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import { ExcelExport, ExcelExportColumn as ExportColumn } from '@progress/kendo-react-excel-export';
import { GridColumn as Column, Grid, GridCustomCellProps, GridDataStateChangeEvent, GridNoRecords, GridToolbar } from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import { Checkbox, NumericTextBox } from '@progress/kendo-react-inputs';
import { arrowRotateCwIcon, filterClearIcon, sparklesIcon } from '@progress/kendo-svg-icons';
import DateCell from 'components/cells/DateCell';
import { CustomerHoverCard } from 'components/CustomerHoverCard';
import MultipleCustomerInput, { CustomerInputValue } from 'components/MultipleCustomerInput';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchApi } from 'services/api';
import { openWindowPerc } from 'services/openWindow';
import { isVisible, visibility } from 'services/visibility';
import { IDName } from 'types/idname';
import { useDebouncedCallback } from 'use-debounce';
import useLocalStorage from 'utils/useLocalStorage';
import { Title } from 'utils/title';
import { dateFormatter } from 'utils/utils';
import { VehicleTypes } from 'views/AssetVehicles/Profile';
import '../LoadBoard/Countdown.css';
import YesNoCell from '../../components/cells/YesNoCell';
import CenterDivPanel from '../../components/CenterDivPanel';
import ActivitySummaryDialog from './ActivitySummaryDialog';

type SortModel = {
  field: string;
  dir: 'asc' | 'desc';
};

type AdvanceSearchParameters = {
  skip: number;
  take: number;
  sortDescriptors: SortModel[];
  activeOnly: boolean;
  isUnassigned?: boolean;
  startDate?: Date;
  endDate?: Date;
  vehicleType?: string;
  billTo?: string;
  shipDate?: Date;
  deliveryDate?: Date;
  createdDate?: Date;
  quoteNumber?: string;
  orderNumber?: string;
  customerIDs?: number[];
  sourceIDs?: number[];
  quoteStatusIDs?: number[];
  vehicleTypeIDs?: number[];
  industryVerticalIDs?: number[];
  bids: boolean;
};

type DataSourceResult<T> = {
  Data: T[];
  Total: number;
};

type SummaryCards = {
  MTD: Totals;
  YTD: Totals;
};

type SummaryResult = SummaryCards & {
  SourceOptions?: IDName[];
  QuoteStatusOptions?: IDName[];
  IndustryVerticalOptions?: IDName[];
};

type Totals = {
  TotalBids: number;
  WonBids: number;
  WinRate: number;
};

type SystemQuoteBidsViewModel = {
  QuoteID: number;
  Source: string;
  IsBlindBid: boolean;
  Unassigned: boolean;
  CreatedByUserName: string | null;
  AssetPAccept: number | null;
  VehiclesAvailable: number;
  AvailablePartnerUnitCount: number | null;
  AvailableLoadOneLiteCount: number | null;
  OrderID: number | null;
  OrderNo: number | null;
  QuoteStatus: string;
  BillToCustomerID: number | null;
  BillToOnCreditHold: boolean;
  BillTo: string;
  CreatedDateTime: string;
  PUDateTime: Date;
  DELDateTime: Date;
  FromCity: string;
  FromState: string;
  ToCity: string;
  ToState: string;
  Miles: number | null;
  VehicleType: string;
  Amount: number | null;
  MinutesSinceBid: number;
  LastCustomerResponseDateTime: string | null;
  ThreadID: string | null;
  SenderEmailAddress: string | null;
};

const formatElapsed = (elapsedSeconds: number) => {
  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  return `${minutes}m ${seconds}s`;
};

const MinutesSinceBidCell = (props: GridCustomCellProps & { now: dayjs.Dayjs }) => {
  if (props.rowType !== 'data') return null;

  const elapsedSeconds = Math.max(0, props.now.diff(dayjs.utc(props.dataItem.CreatedDateTime), 'second'));
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);

  let color = 'countdown-critical';
  if (elapsedMinutes <= 5) {
    color = 'countdown-success';
  } else if (elapsedMinutes <= 10) {
    color = 'countdown-warning';
  }

  return <td {...props.tdProps} className={[props.tdProps?.className, color].filter(Boolean).join(" ")}>{formatElapsed(elapsedSeconds)}</td>;
};

const CustomerReplyCell = (props: GridCustomCellProps & { now: dayjs.Dayjs }) => {
  if (props.rowType !== 'data') return null;
  if (!props.dataItem.LastCustomerResponseDateTime) return <td {...props.tdProps} />;

  const elapsedSeconds = Math.max(0, props.now.diff(dayjs.utc(props.dataItem.LastCustomerResponseDateTime), 'second'));
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  const color = elapsedMinutes <= 5 ? 'countdown-success' : 'countdown-critical';

  return <td {...props.tdProps} className={[props.tdProps?.className, color].filter(Boolean).join(" ")}>{formatElapsed(elapsedSeconds)}</td>;
};

const QuoteIDCell = (props: GridCustomCellProps) => {
  if (props.rowType !== 'data') return null;

  return <td {...props.tdProps}><a style={{ color: props.dataItem.Unassigned ? '#28a745' : '#007bff' }} title={`Quote # ${props.dataItem.QuoteID}`} href={`/Quote/Index/${props.dataItem.QuoteID}`} onClick={event => {
    event.preventDefault();
    openWindowPerc(`/Quote/Index/${props.dataItem.QuoteID}`);
  }}>{props.dataItem.CreatedByUserName || props.dataItem.QuoteID}</a></td>;
};

const ThreadIDCell  = (props: GridCustomCellProps) => {
  if (props.rowType !== 'data') return null;
  const displayValue = props.dataItem.SenderEmailAddress || props.dataItem.ThreadID;

  return (
    <td {...props.tdProps}>
      {(props.dataItem.ThreadID.startsWith("cnv_") || props.dataItem.ThreadID.startsWith("msg_")) ? <a
        target="_blank"
        style={{ color: '#007bff' }}
        href={`https://app.frontapp.com/open/${props.dataItem.ThreadID}`}
      >
        {displayValue}
      </a> : displayValue}
    </td>
  );
}

const ActivitySummaryCell = (props: GridCustomCellProps & { openSummary: (quoteID: number) => void }) => {
  if (props.rowType !== 'data') return null;

  return <td {...props.tdProps}>
    <Button
      fillMode="flat"
      size="small"
      svgIcon={sparklesIcon}
      title="AI summary of what has happened with this quote"
      onClick={() => props.openSummary(props.dataItem.QuoteID)}
    />
  </td>;
};

const OrderNumberCell = (props: GridCustomCellProps) => {
  if (props.rowType !== 'data') return null;
  if (!props.dataItem.OrderNo) return <td {...props.tdProps} />;

  return <td {...props.tdProps}><a style={{ color: '#007bff' }} href={`/Order/${props.dataItem.OrderID}`} onClick={event => {
    event.preventDefault();
    openWindowPerc(`/Order/${props.dataItem.OrderID}`);
  }}>{props.dataItem.OrderNo}</a></td>;
};

const BillToCell = (props: GridCustomCellProps) => {
  if (props.rowType !== 'data') return null;
  if (!props.dataItem.BillToCustomerID) return <td {...props.tdProps}>{props.dataItem.BillTo}</td>;

  return <td {...props.tdProps}><CustomerHoverCard customerId={props.dataItem.BillToCustomerID}><a style={{ color: props.dataItem.BillToOnCreditHold ? 'red' : '#007bff' }} href={`/Customers/Customer/${props.dataItem.BillToCustomerID}`} onClick={event => {
    event.preventDefault();
    openWindowPerc(`/Customers/Customer/${props.dataItem.BillToCustomerID}`);
  }}>{props.dataItem.BillTo}</a></CustomerHoverCard></td>;
};

const FromCell = (props: GridCustomCellProps) => {
  if (props.rowType !== 'data') return null;
  return <td {...props.tdProps}>{`${props.dataItem.FromCity}, ${props.dataItem.FromState}`}</td>;
};

const ToCell = (props: GridCustomCellProps) => {
  if (props.rowType !== 'data') return null;
  return <td {...props.tdProps}>{`${props.dataItem.ToCity}, ${props.dataItem.ToState}`}</td>;
};

const withMinutes = (items: Omit<SystemQuoteBidsViewModel, 'MinutesSinceBid'>[]): SystemQuoteBidsViewModel[] =>
  items.map(item => ({
    ...item,
    MinutesSinceBid: dayjs().diff(dayjs.utc(item.CreatedDateTime), 'minute')
  }));

const EXPORT_MAX_ROWS = 10000;
const POLL_INTERVAL_MS = 30000;
const SUMMARY_POLL_INTERVAL_MS = 300000;
const STALE_REFRESH_MS = 120000;
const getDefaultAllRange = (): SelectionRange => ({
  start: dayjs().subtract(1, 'day').startOf('day').toDate(),
  end: dayjs().startOf('day').toDate()
});

const EMPTY_TOTALS: Totals = {
  TotalBids: 0,
  WonBids: 0,
  WinRate: 0
};

const EMPTY_DATE_RANGE: SelectionRange = {
  start: null,
  end: null
};

const getDefaultDataState = (): { skip: number; take: number; sort: SortDescriptor[] } => ({
  skip: 0,
  take: 20,
  sort: [{ field: 'CreatedDateTime', dir: 'desc' }]
});

const CustomDateInput = (props: DateInputProps) => {
  return (
    <DateInput {...props} label={undefined} width={92} formatPlaceholder={{ year: 'yyyy', month: 'MM', day: 'dd' }} />
  );
};

const defaultSources = [{ ID: 42, Name: 'AI - Auto Create (Email)' }];

const SystemQuoteBids = () => {
  const [loading, setLoading] = React.useState(true);
  const [isTabVisible, setIsTabVisible] = useState(isVisible());
  const [now, setNow] = useState(dayjs());
  const [statusFilter, setStatusFilter] = useLocalStorage<'active' | 'all'>('AIControlTower_StatusFilter', 'active');
  const [dateRange, setDateRange] = useState<SelectionRange>(getDefaultAllRange);
  const [quoteNumberInput, setQuoteNumberInput] = useState<number | null>(null);
  const [quoteNumberFilter, setQuoteNumberFilter] = useState<number | null>(null);
  const [orderNumberInput, setOrderNumberInput] = useState<number | null>(null);
  const [customerFilters, setCustomerFilters] = useLocalStorage<CustomerInputValue[]>('AIControlTower_Customers', []);
  const [orderNumberFilter, setOrderNumberFilter] = useState<number | null>(null);
  const [data, setData] = useState<SystemQuoteBidsViewModel[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<SummaryCards>({ MTD: EMPTY_TOTALS, YTD: EMPTY_TOTALS });
  const [sourceOptions, setSourceOptions] = useState<IDName[]>([]);
  const [sourceFilterInput, setSourceFilterInput] = useLocalStorage<IDName[]>('AIControlTower_SourceFilterInput', defaultSources);
  const [sourceFilters, setSourceFilters] = useLocalStorage<IDName[]>('AIControlTower_SourceFilters', defaultSources);
  const [filteredSourceOptions, setFilteredSourceOptions] = useState<IDName[]>(sourceOptions);
  const [industryVerticalOptions, setIndustryVerticalOptions] = useState<IDName[]>([]);
  const [industryVerticalFilterInput, setIndustryVerticalFilterInput] = useLocalStorage<IDName[]>('AIControlTower_IndustryVerticalFilterInput', []);
  const [industryVerticalFilters, setIndustryVerticalFilters] = useLocalStorage<IDName[]>('AIControlTower_IndustryVerticalFilters', []);
  const [filteredIndustryVerticalOptions, setFilteredIndustryVerticalOptions] = useState<IDName[]>(industryVerticalOptions);
  const [vehicleTypeFilterInput, setVehicleTypeFilterInput] = useLocalStorage<IDName[]>('AIControlTower_VehicleTypeFilterInput', []);
  const [vehicleTypeFilters, setVehicleTypeFilters] = useLocalStorage<IDName[]>('AIControlTower_VehicleTypeFilters', []);
  const [filteredVehicleTypeOptions, setFilteredVehicleTypeOptions] = useState<IDName[]>(VehicleTypes);
  const [quoteStatusOptions, setQuoteStatusOptions] = useState<IDName[]>([]);
  const [quoteStatusFilterInput, setQuoteStatusFilterInput] = useLocalStorage<IDName[]>('AIControlTower_QuoteStatusFilterInput', []);
  const [quoteStatusFilters, setQuoteStatusFilters] = useLocalStorage<IDName[]>('AIControlTower_QuoteStatusFilters', []);
  const [filteredQuoteStatusOptions, setFilteredQuoteStatusOptions] = useState<IDName[]>(quoteStatusOptions);
  const [bidsOnly, setBidsOnly] = useLocalStorage('AIControlTower_BidsOnly', false);
  const [unassignedOnly, setUnassignedOnly] = useLocalStorage('AIControlTower_UnassignedOnly', false);
  const [summaryQuoteID, setSummaryQuoteID] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  const lastRefreshAtRef = useRef<number>(0);
  const refreshRequiredRef = useRef(false);
  const excelExportRef = useRef<ExcelExport | null>(null);

  const [dataState, setDataState] = useState<{ skip: number; take: number; sort: SortDescriptor[] }>(getDefaultDataState);

  const loadSummaryCards = useCallback(() => {
    fetchApi('api/Quote/SystemQuoteBidsSummary')
      .then((response: SummaryResult) => {
        setSummary(previous => ({
          ...previous,
          MTD: response?.MTD ?? EMPTY_TOTALS,
          YTD: response?.YTD ?? EMPTY_TOTALS
        }));
        if ((response?.SourceOptions?.length ?? 0) > 0) {
          setSourceOptions(response.SourceOptions ?? []);
        }
        if ((response?.QuoteStatusOptions?.length ?? 0) > 0) {
          setQuoteStatusOptions(response.QuoteStatusOptions ?? []);
        }
        if ((response?.IndustryVerticalOptions?.length ?? 0) > 0) {
          setIndustryVerticalOptions(response.IndustryVerticalOptions ?? []);
        }
      });
  }, []);

  const buildRequest = useCallback((
    activeDataState: { skip: number; take: number; sort: SortDescriptor[] },
    overrides?: { quoteNumberFilter?: number | null; orderNumberFilter?: number | null; statusFilter?: 'active' | 'all'; dateRange?: SelectionRange }
  ): AdvanceSearchParameters => {
    const activeStatusFilter = overrides?.statusFilter ?? statusFilter;
    const activeDateRange = overrides?.dateRange ?? dateRange;

    const activeQuoteNumberFilter =
      overrides?.quoteNumberFilter !== undefined
        ? overrides.quoteNumberFilter
        : quoteNumberFilter;

    const activeOrderNumberFilter =
      overrides?.orderNumberFilter !== undefined
        ? overrides.orderNumberFilter
        : orderNumberFilter;

    return {
      skip: activeDataState.skip,
      take: activeDataState.take,
      sortDescriptors: (activeDataState.sort ?? []).map(sort => ({
        field: sort.field ?? '',
        dir: sort.dir ?? 'asc'
      })),
      activeOnly: activeStatusFilter === 'active',
      isUnassigned: unassignedOnly,
      startDate: activeStatusFilter === 'all' && activeDateRange.start ? dayjs(activeDateRange.start).startOf('day').toDate() : undefined,
      endDate: activeStatusFilter === 'all' && activeDateRange.end ? dayjs(activeDateRange.end).endOf('day').toDate() : undefined,
      quoteNumber: activeQuoteNumberFilter?.toString(),
      orderNumber: activeOrderNumberFilter?.toString(),
      customerIDs: customerFilters.length > 0 ? customerFilters.map(customer => customer.CustomerID) : undefined,
      sourceIDs: sourceFilters.length > 0 ? sourceFilters.map(source => source.ID) : undefined,
      vehicleTypeIDs: vehicleTypeFilters.length > 0 ? vehicleTypeFilters.map(vehicleType => vehicleType.ID) : undefined,
      industryVerticalIDs: industryVerticalFilters.length > 0 ? industryVerticalFilters.map(vertical => vertical.ID) : undefined,
      quoteStatusIDs: quoteStatusFilters.length > 0 ? quoteStatusFilters.map(status => status.ID) : undefined,
      bids: bidsOnly
    };
  }, [dateRange, orderNumberFilter, quoteNumberFilter, statusFilter, customerFilters, sourceFilters, vehicleTypeFilters, industryVerticalFilters, quoteStatusFilters, bidsOnly, unassignedOnly]);

  const refresh = useCallback((
    force = false,
    nextDataState?: { skip: number; take: number; sort: SortDescriptor[] },
    overrides?: { quoteNumberFilter?: number | null; orderNumberFilter?: number | null; statusFilter?: 'active' | 'all'; dateRange?: SelectionRange }
  ) => {
    if (!force && !isVisible()) {
      refreshRequiredRef.current = true;
      return;
    }

    const activeDataState = nextDataState ?? dataState;
    const activeStatusFilter = overrides?.statusFilter ?? statusFilter;
    const activeDateRange = overrides?.dateRange ?? dateRange;

    if (activeStatusFilter === 'all' && (!activeDateRange.start || !activeDateRange.end)) {
      setData([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    fetchApi('api/Quote/SystemQuoteBids', buildRequest(activeDataState, overrides), 'POST')
      .then((response: DataSourceResult<Omit<SystemQuoteBidsViewModel, 'MinutesSinceBid'>>) => {
        setData(withMinutes(response.Data));
        setTotal(response.Total);
        lastRefreshAtRef.current = Date.now();
        refreshRequiredRef.current = false;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [buildRequest, dataState, dateRange, statusFilter]);

  useEffect(() => {
    setFilteredSourceOptions(sourceOptions);
  }, [sourceOptions]);

  useEffect(() => {
    setFilteredIndustryVerticalOptions(industryVerticalOptions);
  }, [industryVerticalOptions]);

  useEffect(() => {
    setFilteredQuoteStatusOptions(quoteStatusOptions);
  }, [quoteStatusOptions]);

  const vehicleTypeOptions = useMemo(() => VehicleTypes, []);
  useEffect(() => {
    setFilteredVehicleTypeOptions(vehicleTypeOptions);
  }, [vehicleTypeOptions]);

  useEffect(() => {
    loadSummaryCards();
  }, [loadSummaryCards]);

  useEffect(() => {
    refresh(true);
  }, [
    refresh,
    statusFilter,
    dateRange.start,
    dateRange.end,
    quoteNumberFilter,
    orderNumberFilter,
    dataState.skip,
    dataState.take,
    dataState.sort
  ]);

  useEffect(() => {
    if (!isTabVisible) {
      return;
    }

    const interval = setInterval(() => refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isTabVisible, refresh]);

  useEffect(() => {
    if (!isTabVisible) {
      return;
    }

    const interval = setInterval(() => {
      fetchApi('api/Quote/SystemQuoteBidsSummary')
        .then((response: SummaryResult) => {
          setSummary(previous => ({
            ...previous,
            MTD: response?.MTD ?? EMPTY_TOTALS,
            YTD: response?.YTD ?? EMPTY_TOTALS
          }));
        });
    }, SUMMARY_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isTabVisible]);

  useEffect(() => {
    if (!visibility) {
      return;
    }

    const handleVisibilityChange = () => {
      const currentlyVisible = isVisible();
      setIsTabVisible(currentlyVisible);

      if (currentlyVisible) {
        const isStale = Date.now() - lastRefreshAtRef.current >= STALE_REFRESH_MS;
        if (refreshRequiredRef.current || isStale) {
          refresh(true);
        }
      }
    };

    document.addEventListener(visibility.event, handleVisibilityChange);
    return () => document.removeEventListener(visibility.event, handleVisibilityChange);
  }, [refresh]);

  useEffect(() => {
    if (!isTabVisible) {
      return;
    }

    setNow(dayjs());
    const interval = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(interval);
  }, [isTabVisible]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState({
      skip: event.dataState.skip ?? 0,
      take: event.dataState.take ?? dataState.take,
      sort: (event.dataState.sort ?? []) as SortDescriptor[]
    });
  };

  const updateStatus = (nextFilter: 'active' | 'all') => {
    if (nextFilter === statusFilter) {
      return;
    }

    setStatusFilter(nextFilter);
    setDataState(previous => ({
      ...previous,
      skip: 0
    }));
  };

  const updateDateRange = (value: SelectionRange | null) => {
    setDateRange(value ?? EMPTY_DATE_RANGE);
    setDataState(previous => ({
      ...previous,
      skip: 0
    }));
  };

  const columnProps = (field: string) => ({ field });
  const timerCell = useCallback((props: GridCustomCellProps) => <MinutesSinceBidCell {...props} now={now} />, [now]);
  const customerReplyCell = useCallback((props: GridCustomCellProps) => <CustomerReplyCell {...props} now={now} />, [now]);
  const activitySummaryCell = useCallback((props: GridCustomCellProps) => <ActivitySummaryCell {...props} openSummary={setSummaryQuoteID} />, []);

  const resetPageForFilterChange = () => {
    setDataState(previous => ({ ...previous, skip: 0 }));
  };

  const debouncedQuoteFilterChange = useDebouncedCallback((nextQuoteNumber: number | null) => {
    setQuoteNumberFilter(nextQuoteNumber);
    resetPageForFilterChange();
  }, 750);

  const debouncedOrderFilterChange = useDebouncedCallback((nextOrderNumber: number | null) => {
    setOrderNumberFilter(nextOrderNumber);
    resetPageForFilterChange();
  }, 750);

  const debouncedSourceFilterChange = useDebouncedCallback((nextSources: IDName[]) => {
    setSourceFilters(nextSources);
    resetPageForFilterChange();
  }, 750);

  const debouncedIndustryVerticalFilterChange = useDebouncedCallback((nextVerticals: IDName[]) => {
    setIndustryVerticalFilters(nextVerticals);
    resetPageForFilterChange();
  }, 750);

  const debouncedVehicleTypeFilterChange = useDebouncedCallback((nextVehicleTypes: IDName[]) => {
    setVehicleTypeFilters(nextVehicleTypes);
    resetPageForFilterChange();
  }, 750);

  const debouncedQuoteStatusFilterChange = useDebouncedCallback((nextStatuses: IDName[]) => {
    setQuoteStatusFilters(nextStatuses);
    resetPageForFilterChange();
  }, 750);

  const updateQuoteNumberFilter = (nextQuoteNumber: number | null) => {
    setQuoteNumberInput(nextQuoteNumber);
    debouncedQuoteFilterChange(nextQuoteNumber);
  };

  const updateOrderNumberFilter = (nextOrderNumber: number | null) => {
    setOrderNumberInput(nextOrderNumber);
    debouncedOrderFilterChange(nextOrderNumber);
  };

  const updateSourceFilters = (nextSources: IDName[]) => {
    setSourceFilterInput(nextSources);
    debouncedSourceFilterChange(nextSources);
  };

  const handleSourceFilterChange = (event: MultiSelectFilterChangeEvent) => {
    if (!event.filter || !event.filter.value) {
      setFilteredSourceOptions(sourceOptions);
      return;
    }

    setFilteredSourceOptions(filterBy(sourceOptions, event.filter) as IDName[]);
  };

  const updateIndustryVerticalFilters = (nextVerticals: IDName[]) => {
    setIndustryVerticalFilterInput(nextVerticals);
    debouncedIndustryVerticalFilterChange(nextVerticals);
  };

  const handleIndustryVerticalFilterChange = (event: MultiSelectFilterChangeEvent) => {
    if (!event.filter || !event.filter.value) {
      setFilteredIndustryVerticalOptions(industryVerticalOptions);
      return;
    }

    setFilteredIndustryVerticalOptions(filterBy(industryVerticalOptions, event.filter) as IDName[]);
  };

  const updateVehicleTypeFilters = (nextVehicleTypes: IDName[]) => {
    setVehicleTypeFilterInput(nextVehicleTypes);
    debouncedVehicleTypeFilterChange(nextVehicleTypes);
  };

  const handleVehicleTypeFilterChange = (event: MultiSelectFilterChangeEvent) => {
    if (!event.filter || !event.filter.value) {
      setFilteredVehicleTypeOptions(vehicleTypeOptions);
      return;
    }

    setFilteredVehicleTypeOptions(filterBy(vehicleTypeOptions, event.filter) as IDName[]);
  };

  const updateQuoteStatusFilters = (nextStatuses: IDName[]) => {
    setQuoteStatusFilterInput(nextStatuses);
    debouncedQuoteStatusFilterChange(nextStatuses);
  };

  const handleQuoteStatusFilterChange = (event: MultiSelectFilterChangeEvent) => {
    if (!event.filter || !event.filter.value) {
      setFilteredQuoteStatusOptions(quoteStatusOptions);
      return;
    }

    setFilteredQuoteStatusOptions(filterBy(quoteStatusOptions, event.filter) as IDName[]);
  };

  const clearManualFiltersAndRefresh = () => {
    debouncedQuoteFilterChange.cancel();
    debouncedOrderFilterChange.cancel();
    debouncedSourceFilterChange.cancel();
    debouncedIndustryVerticalFilterChange.cancel();
    debouncedVehicleTypeFilterChange.cancel();
    debouncedQuoteStatusFilterChange.cancel();

    setQuoteNumberInput(null);
    setQuoteNumberFilter(null);
    setOrderNumberInput(null);
    setOrderNumberFilter(null);
    setCustomerFilters([]);
    setSourceFilterInput(defaultSources);
    setSourceFilters(defaultSources);
    setFilteredSourceOptions(sourceOptions);
    setIndustryVerticalFilterInput([]);
    setIndustryVerticalFilters([]);
    setFilteredIndustryVerticalOptions(industryVerticalOptions);
    setVehicleTypeFilterInput([]);
    setVehicleTypeFilters([]);
    setFilteredVehicleTypeOptions(vehicleTypeOptions);
    setQuoteStatusFilterInput([]);
    setQuoteStatusFilters([]);
    setFilteredQuoteStatusOptions(quoteStatusOptions);
    setStatusFilter('active');
    setDateRange(getDefaultAllRange());
    setBidsOnly(false);
    setUnassignedOnly(false);
    setDataState(getDefaultDataState());
  };

  const exportToExcel = () => {
    if (statusFilter === 'all' && (!dateRange.start || !dateRange.end)) {
      return;
    }

    setExporting(true);

    const request = buildRequest({ skip: 0, take: EXPORT_MAX_ROWS, sort: dataState.sort });

    fetchApi('api/Quote/SystemQuoteBids', request, 'POST')
      .then((response: DataSourceResult<Omit<SystemQuoteBidsViewModel, 'MinutesSinceBid'>>) => {
        const exportData = response.Data.map(item => ({
          ...item,
          ThreadID: item.SenderEmailAddress || item.ThreadID,
          FromCity: `${item.FromCity}, ${item.FromState}`,
          ToCity: `${item.ToCity}, ${item.ToState}`,
          PUDateTime: dateFormatter(item.PUDateTime),
          DELDateTime: dateFormatter(item.DELDateTime),
          CreatedDateTime: dateFormatter(item.CreatedDateTime)
        }));

        excelExportRef.current?.save(exportData);
      })
      .finally(() => {
        setExporting(false);
      });
  };

  return <>
    <Title string="AI Control Tower" />
    {loading && <CenterDivPanel>
      <Loader type="converging-spinner" />
    </CenterDivPanel>}
    <br />
    <div className="row mb-3">
      <div className="col-md-12 mb-2">
        <div className="card h-100 bg-dark text-white">
          <div className="card-body">
            <h5 className="text-muted fw-normal mt-0">MTD / YTD</h5>
            <h3 className="mt-3 mb-3">{summary.MTD.TotalBids.toLocaleString()}/{summary.YTD.TotalBids.toLocaleString()}</h3>
            <p className="mb-1">Wins: {summary.MTD.WonBids.toLocaleString()}/{summary.YTD.WonBids.toLocaleString()}</p>
            <p className="mb-1">Win Rate: {(summary.MTD.WinRate * 100).toFixed(2)}%/{(summary.YTD.WinRate * 100).toFixed(2)}%</p>
          </div>
        </div>
      </div>
    </div>
    <ExcelExport ref={excelExportRef} fileName="AI Control Tower.xlsx">
      <ExportColumn field="QuoteID" title="Quote #" />
      <ExportColumn field="ThreadID" title="Front" />
      <ExportColumn field="BillTo" title="Bill To" />
      <ExportColumn field="QuoteStatus" title="Status" />
      <ExportColumn field="OrderNo" title="Order #" />
      <ExportColumn field="Source" title="Source" />
      <ExportColumn field="IsBlindBid" title="Blind Bid" />
      <ExportColumn field="AssetPAccept" title="P(Accept)" cellOptions={{ format: '0%' }} />
      <ExportColumn field="VehiclesAvailable" title="L1 INS" />
      <ExportColumn field="AvailablePartnerUnitCount" title="PC INS" />
      <ExportColumn field="AvailableLoadOneLiteCount" title="L1L INS" />
      <ExportColumn field="Miles" title="Miles" cellOptions={{ format: '#,##0' }} />
      <ExportColumn field="Amount" title="Charges" cellOptions={{ format: '$#,##0.00' }} />
      <ExportColumn field="VehicleType" title="Vehicle Type" />
      <ExportColumn field="FromCity" title="From" />
      <ExportColumn field="ToCity" title="To" />
      <ExportColumn field="PUDateTime" title="Ship Date" />
      <ExportColumn field="DELDateTime" title="Delivery Date" />
      <ExportColumn field="CreatedDateTime" title="Created" />
    </ExcelExport>
    <Grid
      reorderable
      dataItemKey="QuoteID"
      sortable={{ allowUnsort: true }}
      pageable={{ pageSizes: [20, 50, 100, 500] }}
      total={total}
      data={data}
      onDataStateChange={dataStateChange}
      {...dataState}
    >
      <GridToolbar overflow="none">
        <Button
          title="Clear Filters and Sort"
          icon="filter-clear"
          svgIcon={filterClearIcon}
          onClick={clearManualFiltersAndRefresh}
        />
        <Button title="Refresh" icon="refresh" svgIcon={arrowRotateCwIcon} onClick={() => refresh(true)} />
        <Button onClick={exportToExcel} disabled={exporting}>{exporting ? 'Exporting…' : 'Excel'}</Button>
        <ButtonGroup>
          <Button togglable selected={statusFilter === 'active'} themeColor={statusFilter === 'active' ? 'primary' : 'base'} onClick={() => updateStatus('active')}>Active</Button>
          <Button togglable selected={statusFilter === 'all'} themeColor={statusFilter === 'all' ? 'primary' : 'base'} onClick={() => updateStatus('all')}>Any</Button>
        </ButtonGroup>
        {statusFilter === 'all' && (
          <DateRangePicker
            style={{ width: 195, gap: '0.3em' }}
            startDateInput={CustomDateInput}
            endDateInput={CustomDateInput}
            value={dateRange}
            onChange={(event) => updateDateRange(event.value)}
          />
        )}
        <Checkbox
          label="Bid Placed"
          value={bidsOnly}
          onChange={(event) => {
            setBidsOnly(event.value);
            resetPageForFilterChange();
          }}
        />
        <Button
          togglable
          selected={unassignedOnly}
          onClick={() => {
            setUnassignedOnly(previous => !previous);
            resetPageForFilterChange();
          }}
        >
          Unassigned
        </Button>
        <NumericTextBox
          value={quoteNumberInput}
          placeholder="Quote Number"
          min={0}
          spinners={false}
          width={150}
          format="0"
          onChange={(event) => updateQuoteNumberFilter(event.value ?? null)}
        />
        <NumericTextBox
          value={orderNumberInput}
          placeholder="Order Number"
          min={0}
          spinners={false}
          width={150}
          format="0"
          onChange={(event) => updateOrderNumberFilter(event.value ?? null)}
        />
        <MultipleCustomerInput
          value={customerFilters}
          onChange={(customers) => {
            setCustomerFilters(customers);
            resetPageForFilterChange();
          }}
          placeholder="Bill To Customers"
        />
        <MultiSelect
          data={filteredSourceOptions}
          value={sourceFilterInput}
          dataItemKey="ID"
          textField="Name"
          placeholder="Sources"
          filterable={true}
          adaptive={true}
          style={{ width: 300 }}
          onFilterChange={handleSourceFilterChange}
          onChange={(event) => updateSourceFilters(event.value as IDName[])}
        />
        <MultiSelect
          data={filteredIndustryVerticalOptions}
          value={industryVerticalFilterInput}
          dataItemKey="ID"
          textField="Name"
          placeholder="Industry Verticals"
          filterable={true}
          adaptive={true}
          style={{ width: 300 }}
          onFilterChange={handleIndustryVerticalFilterChange}
          onChange={(event) => updateIndustryVerticalFilters(event.value as IDName[])}
        />
        <MultiSelect
          data={filteredVehicleTypeOptions}
          value={vehicleTypeFilterInput}
          dataItemKey="ID"
          textField="Name"
          placeholder="Vehicle Types"
          filterable={true}
          adaptive={true}
          style={{ width: 300 }}
          onFilterChange={handleVehicleTypeFilterChange}
          onChange={(event) => updateVehicleTypeFilters(event.value as IDName[])}
        />
        <MultiSelect
          data={filteredQuoteStatusOptions}
          value={quoteStatusFilterInput}
          dataItemKey="ID"
          textField="Name"
          placeholder="Quote Statuses"
          filterable={true}
          adaptive={true}
          style={{ width: 300 }}
          onFilterChange={handleQuoteStatusFilterChange}
          onChange={(event) => updateQuoteStatusFilters(event.value as IDName[])}
        />
      </GridToolbar>
      <GridNoRecords>{statusFilter === 'active' ? 'No active system bids.' : 'No system bids found for selected range.'}</GridNoRecords>
      <Column {...columnProps('QuoteID')} title="Quote #" cells={{ data: QuoteIDCell }} />
      <Column title=" Recap" sortable={false} width={54} cells={{ data: activitySummaryCell }} />
      <Column {...columnProps('ThreadID')} title="Front" sortable={false} cells={{ data: ThreadIDCell }} />
      <Column {...columnProps('BillTo')} title="Bill To" cells={{ data: BillToCell }} width={78} />
      <Column {...columnProps('QuoteStatus')} title="Status" />
      <Column {...columnProps('LastCustomerResponseDateTime')} title="Customer Reply" cells={{ data: customerReplyCell }} />
      <Column {...columnProps('OrderNo')} title="Order #" cells={{ data: OrderNumberCell }} />
      <Column {...columnProps('Source')} title="Source" />
      <Column {...columnProps('IsBlindBid')} title="Blind Bid" cells={{ data: YesNoCell }} width={68} />
      <Column {...columnProps('AssetPAccept')} title="P(Accept)" format="{0:p0}" width={74} />
      <Column {...columnProps('VehiclesAvailable')} title="L1 INS" width={54} />
      <Column {...columnProps('AvailablePartnerUnitCount')} title="PC INS" width={58} />
      <Column {...columnProps('AvailableLoadOneLiteCount')} title="L1L INS" width={60} />
      <Column {...columnProps('Miles')} title="Miles" format="{0:n0}" width={50} />
      <Column {...columnProps('Amount')} title="Charges" format="{0:c2}" width={78} />
      <Column {...columnProps('VehicleType')} title="Vehicle Type" />
      <Column {...columnProps('FromCity')} title="From" cells={{ data: FromCell }} />
      <Column {...columnProps('ToCity')} title="To" cells={{ data: ToCell }} />
      <Column {...columnProps('PUDateTime')} title="Ship Date" cells={{ data: DateCell }} />
      <Column {...columnProps('DELDateTime')} title="Delivery Date" cells={{ data: DateCell }} />
      <Column {...columnProps('CreatedDateTime')} title="Created" cells={{ data: DateCell }} />
      <Column {...columnProps('MinutesSinceBid')} title="Timer" sortable={false} cells={{ data: timerCell }} />
    </Grid>
    {summaryQuoteID !== null && <ActivitySummaryDialog QuoteID={summaryQuoteID} CloseDialog={() => setSummaryQuoteID(null)} />}
  </>;
};

export default SystemQuoteBids;
